import type { ReactNode } from 'react'

import { useTheme } from '../../theme/ThemeContext'
import { ThemeModal } from '../ThemeModal'

type BlockingModalProps = {
  open: boolean
  title?: string
  message?: ReactNode
  detail?: ReactNode
  stackLevel?: 'base' | 'nested'
  manageBodyScroll?: boolean
}

/**
 * 请求中的同步阻塞弹窗：不可关闭、不可点透，用于防止用户在关键请求期间重复提交。
 */
export function BlockingModal({
  open,
  title = '处理中',
  message = '请求正在处理，请稍候…',
  detail,
  stackLevel = 'nested',
  manageBodyScroll = false,
}: BlockingModalProps) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const ringShape = angular ? 'rounded-none border-2' : 'rounded-full'
  const dotShape = angular ? 'rounded-none' : 'rounded-full'

  return (
    <ThemeModal
      open={open}
      onClose={() => undefined}
      title={title}
      angular={angular}
      size="sm"
      stackLevel={stackLevel}
      closeOnEscape={false}
      manageBodyScroll={manageBodyScroll}
      dismissible={false}
      bodyClassName="px-5 py-6 sm:px-6 sm:py-7"
    >
      <div className="flex flex-col items-center gap-4 text-center" aria-busy="true">
        <div
          className={[
            'relative grid size-14 place-items-center border border-line bg-surface-muted/60 text-accent shadow-sm',
            ringShape,
          ].join(' ')}
          aria-hidden
        >
          <span
            className={[
              'absolute inset-2 border-2 border-accent/25 border-t-accent motion-safe:animate-spin',
              ringShape,
            ].join(' ')}
          />
          <span className={['size-2.5 bg-accent shadow-[0_0_18px_var(--accent)]', dotShape].join(' ')} />
        </div>
        <div className="space-y-1.5">
          <p className="text-sm font-semibold leading-relaxed text-ink">{message}</p>
          {detail ? <p className="text-xs leading-relaxed text-ink-soft">{detail}</p> : null}
        </div>
      </div>
    </ThemeModal>
  )
}
